
import React, { useRef, useEffect, useState } from "react";
import { Modal, Button, Alert } from "react-bootstrap";
import * as bodyPix from "@tensorflow-models/body-pix";
import "@tensorflow/tfjs";
import "../styles/CameraCapture.css";

const CameraCapture = ({ show, onClose, onCapture, title, documentType }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const modelRef = useRef(null);
  const [cameraError, setCameraError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState("Iniciando cámara...");
  const [useSimpleCapture, setUseSimpleCapture] = useState(false);

  const GUIDE_WIDTH = 320;
  const GUIDE_HEIGHT = 220;
  const CAMERA_TIMEOUT = 15000; // 15 segundos timeout

  useEffect(() => {
    if (show) {
      setCameraError(null);
      setIsLoading(true);
      setLoadingMessage("Iniciando cámara...");
      setUseSimpleCapture(false);

      // ============ DIAGNÓSTICO COMPLETO ============
      console.log("\n" + "=".repeat(60));
      console.log("📸 INICIANDO DIAGNÓSTICO DE CÁMARA");
      console.log("=".repeat(60));
      
      // 1. Información del navegador y entorno
      console.log("\n🔍 INFORMACIÓN DEL ENTORNO:");
      console.log("  📱 User Agent:", navigator.userAgent);
      console.log("  🌐 URL actual:", window.location.href);
      console.log("  🔒 Protocolo:", window.location.protocol, window.location.protocol === "https:" ? "✅ (HTTPS OK)" : "❌ (REQUIERE HTTPS PARA CÁMARA)");
      console.log("  🖥️  Hostname:", window.location.hostname);
      console.log("  📄 Tipo documento:", documentType);
      
      // 2. Verificación de soporte del navegador
      console.log("\n🔍 VERIFICACIÓN DE SOPORTE:");
      console.log("  🎥 navigator.mediaDevices:", !!navigator.mediaDevices ? "✅ Disponible" : "❌ NO disponible");
      console.log("  📹 getUserMedia:", !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia) ? "✅ Disponible" : "❌ NO disponible");
      
      // 3. Verificación de permisos (si está disponible)
      if (navigator.permissions && navigator.permissions.query) {
        navigator.permissions.query({ name: 'camera' }).then(permissionStatus => {
          console.log("  🔐 Permiso de cámara:", permissionStatus.state);
          if (permissionStatus.state === 'denied') {
            console.error("  ❌❌❌ PERMISO DENEGADO - El usuario debe habilitar la cámara en la configuración del navegador");
          } else if (permissionStatus.state === 'granted') {
            console.log("  ✅✅✅ Permiso previamente otorgado");
          } else {
            console.log("  ⏳ Permiso pendiente - Se solicitará al usuario");
          }
        }).catch(err => {
          console.log("  ⚠️  No se pudo verificar permiso:", err.message);
        });
      } else {
        console.log("  ⚠️  API de permisos no disponible en este navegador");
      }
      
      // 4. Advertencia de HTTPS
      if (window.location.protocol !== "https:" && window.location.hostname !== "localhost" && window.location.hostname !== "127.0.0.1") {
        console.error("\n❌❌❌ ERROR CRÍTICO: HTTPS ES OBLIGATORIO ❌❌❌");
        console.error("El navegador bloqueará el acceso a la cámara sin HTTPS.");
        console.error("Solución: Inicia el servidor con: npm start (HTTPS=true en .env)");
      }
      
      console.log("\n" + "=".repeat(60) + "\n");

      // CRÍTICO: Polling para esperar a que el DOM esté listo
      // El Modal de Bootstrap tiene animación de entrada que puede tardar
      let attempts = 0;
      const maxAttempts = 20; // 20 intentos x 100ms = 2 segundos máximo
      
      const checkVideoRef = () => {
        attempts++;
        console.log(`🕐 Intento ${attempts}/${maxAttempts} - Verificando videoRef...`);
        
        if (videoRef.current) {
          console.log("✅ videoRef.current existe, procediendo a solicitar cámara");
          initCamera();
        } else if (attempts >= maxAttempts) {
          console.error("❌ CRÍTICO: videoRef.current sigue siendo null después de todos los intentos");
          setCameraError("❌ Error al inicializar el componente de video. Intenta cerrar y abrir nuevamente.");
          setIsLoading(false);
        } else {
          // Intentar nuevamente después de 100ms
          setTimeout(checkVideoRef, 100);
        }
      };
      
      // Iniciar el polling
      setTimeout(checkVideoRef, 100);
      
      const initCamera = () => {
        
        // Timeout para evitar carga infinita
        const cameraTimeout = setTimeout(() => {
          console.error("⏱️ TIMEOUT: La cámara tardó demasiado en responder");
          setCameraError("⏱️ La cámara tardó demasiado en responder. Verifica los permisos y vuelve a intentar.");
          setIsLoading(false);
        }, CAMERA_TIMEOUT);

        // Verificar disponibilidad de MediaDevices
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          clearTimeout(cameraTimeout);
          console.error("❌ MediaDevices no disponible en este navegador");
          
          let errorMsg = "❌ Este navegador no soporta acceso a la cámara.";
          
          // Verificar si es problema de HTTPS
          if (window.location.protocol !== "https:" && window.location.hostname !== "localhost" && window.location.hostname !== "127.0.0.1") {
            errorMsg = "❌ REQUIERE HTTPS: El acceso a la cámara solo funciona con HTTPS.\n\n" +
                      "📝 Soluciones:\n" +
                      "1. En desarrollo: Asegúrate que .env tenga HTTPS=true\n" +
                      "2. Ejecuta: npm start\n" +
                      "3. En producción: Usa solo dominios HTTPS";
          } else {
            errorMsg += "\n\nIntenta usar Chrome, Firefox o Safari actualizado.";
          }
          
          setCameraError(errorMsg);
          setIsLoading(false);
          return;
        }

        // Intentar primero con la cámara preferida
        const preferredConstraints = {
          video: {
            width: { ideal: 1280 },
            height: { ideal: 720 },
            facingMode: documentType === "selfie" ? "user" : "environment"
          },
        };

        // Fallback: si falla, intentar con cualquier cámara disponible
        const fallbackConstraints = {
          video: {
            width: { ideal: 1280 },
            height: { ideal: 720 }
          },
        };

        console.log("🎥 Solicitando acceso a cámara con constraints:", preferredConstraints);

        navigator.mediaDevices
          .getUserMedia(preferredConstraints)
          .then((stream) => {
          clearTimeout(cameraTimeout);
          console.log("✅ Cámara preferida obtenida exitosamente");
          console.log("🔍 videoRef.current existe?", !!videoRef.current);
          
          // Timeout de seguridad: SIEMPRE se ejecuta después de 3 segundos
          const videoTimeout = setTimeout(() => {
            console.warn("⚠️ TIMEOUT DE SEGURIDAD: Forzando visualización después de 3 segundos");
            setIsLoading(false);
            setLoadingMessage("");
          }, 3000);
          
          if (videoRef.current) {
            console.log("📹 Asignando stream al video element...");
            videoRef.current.srcObject = stream;
            
            // Registrar eventos ANTES de cualquier otra cosa
            const videoElement = videoRef.current;
            
            const handleLoadedMetadata = () => {
              console.log("📹 ✅ onloadedmetadata disparado");
              clearTimeout(videoTimeout);
              videoElement.play()
                .then(() => {
                  console.log("✅ Video reproduciéndose correctamente");
                  setIsLoading(false);
                  setLoadingMessage("");
                })
                .catch(err => {
                  console.error("❌ Error reproduciendo video:", err);
                  setIsLoading(false);
                  setLoadingMessage("");
                });
            };
            
            const handleCanPlay = () => {
              console.log("📹 ✅ oncanplay disparado");
              clearTimeout(videoTimeout);
              setIsLoading(false);
              setLoadingMessage("");
            };
            
            videoElement.addEventListener('loadedmetadata', handleLoadedMetadata);
            videoElement.addEventListener('canplay', handleCanPlay);
            
            console.log("📹 Event listeners registrados");
            
            // Verificar si el video ya tiene readyState válido
            console.log("📹 Video readyState:", videoElement.readyState);
            if (videoElement.readyState >= 2) {
              console.log("📹 Video ya está listo (readyState >= 2), disparando inmediatamente");
              clearTimeout(videoTimeout);
              setIsLoading(false);
              setLoadingMessage("");
            }
          } else {
            console.error("❌ videoRef.current NO EXISTE al asignar stream");
          }
        })
        .catch((err) => {
          console.warn("⚠️ No se pudo usar la cámara preferida:", err.name, err.message);
          console.log("🔄 Intentando con cámara fallback...");
          
          // Intentar con cualquier cámara disponible
          navigator.mediaDevices
            .getUserMedia(fallbackConstraints)
            .then((stream) => {
              clearTimeout(cameraTimeout);
              console.log("✅ Cámara fallback obtenida exitosamente");
              console.log("🔍 Fallback - videoRef.current existe?", !!videoRef.current);
              
              // Timeout de seguridad SIEMPRE se ejecuta
              const videoTimeout = setTimeout(() => {
                console.warn("⚠️ TIMEOUT FALLBACK: Forzando visualización después de 3 segundos");
                setIsLoading(false);
                setLoadingMessage("");
              }, 3000);
              
              if (videoRef.current) {
                console.log("📹 Fallback - Asignando stream al video element...");
                videoRef.current.srcObject = stream;
                
                const videoElement = videoRef.current;
                
                const handleLoadedMetadata = () => {
                  console.log("📹 ✅ Fallback - onloadedmetadata disparado");
                  clearTimeout(videoTimeout);
                  videoElement.play()
                    .then(() => {
                      console.log("✅ Fallback - Video reproduciéndose correctamente");
                      setIsLoading(false);
                      setLoadingMessage("");
                    })
                    .catch(err => {
                      console.error("❌ Fallback - Error reproduciendo video:", err);
                      setIsLoading(false);
                      setLoadingMessage("");
                    });
                };
                
                const handleCanPlay = () => {
                  console.log("📹 ✅ Fallback - oncanplay disparado");
                  clearTimeout(videoTimeout);
                  setIsLoading(false);
                  setLoadingMessage("");
                };
                
                videoElement.addEventListener('loadedmetadata', handleLoadedMetadata);
                videoElement.addEventListener('canplay', handleCanPlay);
                
                console.log("📹 Fallback - Event listeners registrados");
                
                // Verificar readyState
                console.log("📹 Fallback - Video readyState:", videoElement.readyState);
                if (videoElement.readyState >= 2) {
                  console.log("📹 Fallback - Video ya está listo, disparando inmediatamente");
                  clearTimeout(videoTimeout);
                  setIsLoading(false);
                  setLoadingMessage("");
                }
              } else {
                console.error("❌ Fallback - videoRef.current NO EXISTE");
              }
            })
            .catch((fallbackErr) => {
              clearTimeout(cameraTimeout);
              console.error("❌ Error definitivo al abrir cámara:", fallbackErr.name, fallbackErr.message);
              console.error("📋 Detalles completos del error:", fallbackErr);
              
              let errorMessage = "No se pudo acceder a la cámara.";
              let errorDetails = "";
              
              if (fallbackErr.name === "NotFoundError" || fallbackErr.name === "DevicesNotFoundError") {
                errorMessage = "❌ No se detectó ninguna cámara conectada.";
                errorDetails = "Verifica que tu dispositivo tenga una cámara habilitada.";
              } else if (fallbackErr.name === "NotAllowedError" || fallbackErr.name === "PermissionDeniedError") {
                errorMessage = "❌ Permiso denegado para acceder a la cámara.";
                errorDetails = "Debes permitir el acceso a la cámara en la configuración del navegador. En móviles, verifica Configuración > Permisos del sitio.";
              } else if (fallbackErr.name === "NotReadableError" || fallbackErr.name === "TrackStartError") {
                errorMessage = "❌ La cámara está siendo usada por otra aplicación.";
                errorDetails = "Cierra otras aplicaciones que puedan estar usando la cámara.";
              } else if (fallbackErr.name === "OverconstrainedError") {
                errorMessage = "❌ No se encontró una cámara compatible.";
                errorDetails = "Tu dispositivo no tiene una cámara que cumpla los requisitos.";
              } else if (fallbackErr.name === "TypeError") {
                errorMessage = "❌ Error de configuración del navegador.";
                errorDetails = "Asegúrate de estar usando HTTPS y tener permisos habilitados.";
              }
              
              setCameraError(`${errorMessage}\n\n${errorDetails}\n\n🔍 Error técnico: ${fallbackErr.name}`);
              setIsLoading(false);
              setLoadingMessage("");
            });
        });

        // Cargar BodyPix solo para selfies (de forma asíncrona)
        if (documentType === "selfie") {
          setLoadingMessage("Cargando procesador de imagen...");
          console.log("🤖 Cargando modelo BodyPix para selfies...");
          
          const modelTimeout = setTimeout(() => {
            console.warn("⏱️ Timeout cargando BodyPix, usando captura simple");
            setUseSimpleCapture(true);
            setLoadingMessage("Iniciando cámara...");
          }, 10000);

          bodyPix
            .load({
              architecture: "MobileNetV1",
              outputStride: 16,
              multiplier: 0.75,
              quantBytes: 2,
            })
            .then((net) => {
              clearTimeout(modelTimeout);
              modelRef.current = net;
              console.log("✅ Modelo BodyPix cargado exitosamente");
              setLoadingMessage("Iniciando cámara...");
            })
            .catch((err) => {
              clearTimeout(modelTimeout);
              console.error("❌ Error cargando BodyPix:", err);
              console.warn("⚠️ Usando captura simple sin fondo personalizado");
              setUseSimpleCapture(true);
              setLoadingMessage("Iniciando cámara...");
            });
        }
      }; // Fin de initCamera()
    }

    return () => {
      if (videoRef.current && videoRef.current.srcObject) {
        videoRef.current.srcObject.getTracks().forEach((track) => track.stop());
      }
    };
  }, [show, documentType]);

  const handleCapture = async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (!video || !canvas) {
      alert("❌ Error: No se pudo acceder al video o canvas");
      return;
    }

    // Validar que el video tenga dimensiones válidas
    if (video.videoWidth === 0 || video.videoHeight === 0) {
      alert("❌ Error: La cámara no está transmitiendo correctamente. Por favor, verifica que esté conectada.");
      return;
    }

    console.log("📷 Capturando imagen - Dimensiones:", video.videoWidth, "x", video.videoHeight);

    const context = canvas.getContext("2d");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // 👉 Selfie con fondo celeste (solo si BodyPix está disponible)
    if (documentType === "selfie" && modelRef.current && !useSimpleCapture) {
      try {
        console.log("🤖 Aplicando segmentación con BodyPix...");
        const segmentation = await modelRef.current.segmentPerson(video, {
          internalResolution: "medium",
          segmentationThreshold: 0.7,
        });

        const { width, height, data: mask } = segmentation;

        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
        const pixels = imageData.data;

        for (let i = 0; i < mask.length; i++) {
          if (mask[i] === 0) {
            pixels[i * 4] = 135;     // R
            pixels[i * 4 + 1] = 206; // G
            pixels[i * 4 + 2] = 235; // B
            pixels[i * 4 + 3] = 255; // A
          }
        }

        context.putImageData(imageData, 0, 0);

        const finalImage = canvas.toDataURL("image/png");
        console.log("✅ Selfie con fondo personalizado capturada");
        onCapture(finalImage);
        onClose();
        return;
      } catch (error) {
        console.error("❌ Error al aplicar BodyPix, usando captura simple:", error);
        // Continuar con captura simple
      }
    }

    // 👉 Captura simple para selfies sin BodyPix o documentos con recorte
    if (documentType === "selfie") {
      // Selfie simple sin procesamiento
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      const imageData = canvas.toDataURL("image/png");
      console.log("✅ Selfie simple capturada");
      onCapture(imageData);
      onClose();
      return;
    }

    // 👉 Documentos con recorte rectangular
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    const videoRect = video.getBoundingClientRect();
    const guideX = (videoRect.width - GUIDE_WIDTH) / 2;
    const guideY = (videoRect.height - GUIDE_HEIGHT) / 2;

    const scaleX = canvas.width / videoRect.width;
    const scaleY = canvas.height / videoRect.height;

    const cropX = guideX * scaleX;
    const cropY = guideY * scaleY;
    const cropW = GUIDE_WIDTH * scaleX;
    const cropH = GUIDE_HEIGHT * scaleY;

    const croppedCanvas = document.createElement("canvas");
    croppedCanvas.width = cropW;
    croppedCanvas.height = cropH;
    const croppedCtx = croppedCanvas.getContext("2d");

    croppedCtx.drawImage(canvas, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);

    const imageData = croppedCanvas.toDataURL("image/png");
    console.log("✅ Documento capturado y recortado");
    onCapture(imageData);
    onClose();
  };

  return (
    <Modal show={show} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="text-center position-relative">
        {cameraError ? (
          <Alert variant="danger" className="m-3">
            <div style={{ whiteSpace: 'pre-line', textAlign: 'left' }}>
              {cameraError}
            </div>
            <hr />
            <div className="text-start">
              <p className="mb-2"><strong>💡 Sugerencias:</strong></p>
              <ul className="mb-0" style={{ fontSize: '0.9em' }}>
                <li>Verifica que hayas dado permisos de cámara al navegador</li>
                <li>En móviles: Configuración → Permisos del sitio → Cámara</li>
                <li>Asegúrate de que ninguna otra app esté usando la cámara</li>
                <li>Recarga la página e intenta nuevamente</li>
                <li>Si usas un navegador privado, algunas funciones pueden estar bloqueadas</li>
              </ul>
            </div>
            <hr />
            <Button 
              variant="primary" 
              size="sm"
              onClick={() => window.location.reload()}
              className="mt-2"
            >
              🔄 Recargar página
            </Button>
          </Alert>
        ) : isLoading ? (
          <div className="py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Cargando...</span>
            </div>
            <p className="mt-3 fw-bold">{loadingMessage || "Cargando cámara..."}</p>
            {documentType === "selfie" && loadingMessage.includes("procesador") && (
              <small className="text-muted d-block mt-2">
                Esto puede tardar unos segundos en dispositivos móviles...
              </small>
            )}
            <small className="text-muted d-block mt-3">
              ⏱️ Si tarda más de 15 segundos, verifica los permisos
            </small>
          </div>
        ) : null}
        
        {/* Video siempre renderizado (oculto durante carga/error) */}
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="w-100 rounded"
          style={{ 
            maxHeight: "70vh",
            display: (cameraError || isLoading) ? "none" : "block"
          }}
        />
        <canvas ref={canvasRef} style={{ display: "none" }} />
        
        {/* Interfaz de cámara lista */}
        {!cameraError && !isLoading && (
          <>

            {/* Instrucciones flotantes */}
            {documentType !== "selfie" ? (
              <>
                <div className="camera-instructions">
                  <div className="icon">📄</div>
                  <div className="main-text">Centrar documento en el marco</div>
                  <div className="sub-text">Asegúrate de que esté completo y legible</div>
                </div>
                <div
                  className="camera-overlay"
                  style={{
                    width: `${GUIDE_WIDTH}px`,
                    height: `${GUIDE_HEIGHT}px`,
                  }}
                >
                  {/* Esquinas para alineación */}
                  <div className="corner-tl"></div>
                  <div className="corner-tr"></div>
                  <div className="corner-bl"></div>
                  <div className="corner-br"></div>
                </div>
                <div className="capture-quality">
                  <div className="quality-indicator"></div>
                  <span>Listo para capturar</span>
                </div>
              </>
            ) : (
              <div className="camera-instructions">
                <div className="icon">🤳</div>
                <div className="main-text">Centra tu rostro</div>
                <div className="sub-text">
                  {useSimpleCapture 
                    ? "Se capturará con el fondo actual" 
                    : "El fondo se reemplazará automáticamente"}
                </div>
              </div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Cancelar
        </Button>
        <Button 
          variant="primary" 
          onClick={handleCapture}
          disabled={cameraError || isLoading}
        >
          Capturar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CameraCapture;
